import { createFileRoute } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { adminRecheckOrder } from "@/lib/payments.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCw, Search } from "lucide-react";

export const Route = createFileRoute("/admin/automatic-payments")({ component: AutomaticPaymentsAdmin });

type OrderRow = {
  id: string;
  internal_order_id: string;
  gateway_name: string;
  gateway_order_id: string | null;
  user_id: string;
  customer_mobile: string | null;
  requested_amount: number;
  confirmed_amount: number | null;
  status: string;
  environment: string;
  utr: string | null;
  wallet_credited: boolean;
  credited_at: string | null;
  failure_reason: string | null;
  created_at: string;
};

const STATUS_STYLES: Record<string, string> = {
  success: "bg-emerald-500/15 text-emerald-600",
  pending: "bg-amber-500/15 text-amber-600",
  created: "bg-muted text-muted-foreground",
  failed: "bg-destructive/15 text-destructive",
  expired: "bg-destructive/10 text-destructive",
  cancelled: "bg-destructive/10 text-destructive",
  amount_mismatch: "bg-amber-500/20 text-amber-700",
  review_required: "bg-amber-500/20 text-amber-700",
};

function AutomaticPaymentsAdmin() {
  const qc = useQueryClient();
  const recheck = useServerFn(adminRecheckOrder);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [busy, setBusy] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["admin_payment_orders"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("payment_orders")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(500);
      if (error) throw error;
      return (data ?? []) as OrderRow[];
    },
  });

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data ?? []).filter((r) => {
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      if (!q) return true;
      return [r.internal_order_id, r.gateway_order_id, r.utr, r.customer_mobile]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [data, search, statusFilter]);

  const totals = useMemo(() => {
    const credited = rows.filter((r) => r.wallet_credited);
    return {
      count: rows.length,
      creditedCount: credited.length,
      creditedAmount: credited.reduce((s, r) => s + Number(r.confirmed_amount ?? r.requested_amount), 0),
      pending: rows.filter((r) => ["created", "pending"].includes(r.status)).length,
      review: rows.filter((r) => ["amount_mismatch", "review_required"].includes(r.status)).length,
    };
  }, [rows]);

  async function onRecheck(orderId: string) {
    setBusy(orderId);
    try {
      const res = await recheck({ data: { internalOrderId: orderId } });
      if (!res.ok) toast.error(res.code ?? "Re-check failed");
      else toast.success(res.data?.message ?? "Re-checked");
      qc.invalidateQueries({ queryKey: ["admin_payment_orders"] });
    } catch {
      toast.error("Re-check failed");
    } finally {
      setBusy(null);
    }
  }

  function exportCsv() {
    const header = ["Order ID", "Gateway Order ID", "Mobile", "Requested", "Confirmed", "Status", "UTR", "Credited", "Environment", "Created"];
    const lines = rows.map((r) => [
      r.internal_order_id,
      r.gateway_order_id ?? "",
      r.customer_mobile ?? "",
      r.requested_amount,
      r.confirmed_amount ?? "",
      r.status,
      r.utr ?? "",
      r.wallet_credited ? "Yes" : "No",
      r.environment,
      new Date(r.created_at).toLocaleString("en-IN"),
    ]);
    const csv = [header, ...lines].map((l) => l.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `automatic-payments-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-2xl font-black">Automatic Payments</h1>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => qc.invalidateQueries({ queryKey: ["admin_payment_orders"] })}>
            <RefreshCw className="mr-2 h-3.5 w-3.5" /> Refresh
          </Button>
          <Button size="sm" onClick={exportCsv} disabled={!rows.length}>Export CSV</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Stat label="Orders" value={String(totals.count)} />
        <Stat label="Credited" value={`${totals.creditedCount}`} />
        <Stat label="Credited Amount" value={`₹${totals.creditedAmount.toLocaleString("en-IN")}`} />
        <Stat label="Pending / Review" value={`${totals.pending} / ${totals.review}`} />
      </div>

      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-9" placeholder="Search order ID, UTR or mobile" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="h-10 rounded-md border border-input bg-background px-3 text-sm"
        >
          {["all", "created", "pending", "success", "failed", "expired", "cancelled", "amount_mismatch", "review_required"].map((s) => (
            <option key={s} value={s}>{s === "all" ? "All statuses" : s.replace(/_/g, " ")}</option>
          ))}
        </select>
      </div>

      <div className="overflow-x-auto rounded-xl border bg-card">
        <table className="w-full min-w-[860px] text-sm">
          <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
            <tr>
              <th className="p-3">Order</th>
              <th className="p-3">Mobile</th>
              <th className="p-3">Amount</th>
              <th className="p-3">Status</th>
              <th className="p-3">UTR</th>
              <th className="p-3">Credited</th>
              <th className="p-3">Date</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={8} className="p-6 text-center text-muted-foreground"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></td></tr>
            )}
            {!isLoading && !rows.length && (
              <tr><td colSpan={8} className="p-6 text-center text-muted-foreground">No automatic payments yet.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="border-t">
                <td className="p-3">
                  <p className="font-semibold">{r.internal_order_id}</p>
                  <p className="text-[11px] uppercase text-muted-foreground">{r.gateway_name} · {r.environment}</p>
                </td>
                <td className="p-3">{r.customer_mobile ?? "—"}</td>
                <td className="p-3">
                  ₹{Number(r.requested_amount).toLocaleString("en-IN")}
                  {r.confirmed_amount != null && Number(r.confirmed_amount) !== Number(r.requested_amount) && (
                    <span className="block text-[11px] text-destructive">paid ₹{Number(r.confirmed_amount).toLocaleString("en-IN")}</span>
                  )}
                </td>
                <td className="p-3">
                  <span className={"rounded-full px-2 py-0.5 text-[11px] font-semibold capitalize " + (STATUS_STYLES[r.status] ?? "bg-muted")}>
                    {r.status.replace(/_/g, " ")}
                  </span>
                  {r.failure_reason && <p className="mt-1 max-w-[220px] truncate text-[11px] text-muted-foreground">{r.failure_reason}</p>}
                </td>
                <td className="p-3 text-xs">{r.utr ?? "—"}</td>
                <td className="p-3 text-xs">{r.wallet_credited ? `Yes · ${r.credited_at ? new Date(r.credited_at).toLocaleString("en-IN") : ""}` : "No"}</td>
                <td className="p-3 text-xs">{new Date(r.created_at).toLocaleString("en-IN")}</td>
                <td className="p-3">
                  <Button size="sm" variant="outline" disabled={busy === r.internal_order_id || r.wallet_credited} onClick={() => onRecheck(r.internal_order_id)}>
                    {busy === r.internal_order_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Re-check"}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-[11px] text-muted-foreground">
        Wallets are credited exactly once by a secured database function. Re-checking a paid order can never credit it twice.
      </p>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border bg-card p-3">
      <p className="text-[11px] uppercase text-muted-foreground">{label}</p>
      <p className="text-lg font-black">{value}</p>
    </div>
  );
}
